import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdCard,
  faShieldAlt,
  faCheckCircle,
  faCircleExclamation,
  faEye,
  faEyeSlash,
  faLock,
  faIdBadge,
  faCamera,
} from '@fortawesome/free-solid-svg-icons';
import PhotoChange from '../components/ProfilePhoto/PhotoChange';
import PublicProfileForm from '../components/TeacherCard/PublicProfileForm'; // "Meet our teachers" card
import './Settings.css';

const API = process.env.REACT_APP_API_BASE_URL;
const MIN_PASSWORD = 6;

const Message = ({ msg }) =>
  msg?.text ? (
    <p className={`rmtset-msg is-${msg.type}`} role={msg.type === 'error' ? 'alert' : 'status'}>
      <FontAwesomeIcon icon={msg.type === 'success' ? faCheckCircle : faCircleExclamation} /> {msg.text}
    </p>
  ) : null;

// the page's tabs ("?tab=photo" opens one directly)
const TABS = [
  ['card', 'Home page card', faIdBadge],
  ['photo', 'Photo', faCamera],
  ['details', 'My details', faIdCard],
  ['password', 'Password', faShieldAlt],
];
const tabFromUrl = () => {
  try {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TABS.some(([k]) => k === t) ? t : 'card';
  } catch {
    return 'card';
  }
};

const TeacherSettings = ({ userData }) => {
  const teacherId = userData?.id;
  const [tab, setTab] = useState(tabFromUrl);
  const openTab = (key) => {
    setTab(key);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', key);
      window.history.replaceState(null, '', url);
    } catch {
      /* old browsers: the tab still opens */
    }
  };

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);
  const [profile, setProfile] = useState(null); // read-only fields

  // date of birth
  const [dob, setDob] = useState('');
  const [savedDob, setSavedDob] = useState('');
  const [savingDob, setSavingDob] = useState(false);
  const [dobMsg, setDobMsg] = useState(null);

  // password
  const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  // ---------- Load (same endpoint) ----------
  useEffect(() => {
    if (!teacherId) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        const res = await axios.get(`${API}/api/teacher/teachersettings/${teacherId}`);
        const t = res.data?.teacher || {};
        if (cancelled) return;
        const birth = t.birth ? String(t.birth).split('T')[0] : '';
        setProfile({
          username: userData?.username || '',
          firstName: t.firstName || '',
          lastName: t.lastName || '',
          email: t.email || '',
          mobileNumber: t.mobileNumber || '',
        });
        setDob(birth);
        setSavedDob(birth);
      } catch (err) {
        console.error('Error loading teacher settings:', err);
        if (!cancelled) setLoadError('We couldn’t load your details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teacherId, userData?.username, reload]);

  // ---------- Date of birth (same endpoint + payload) ----------
  const saveDob = async (e) => {
    e.preventDefault();
    if (dob === savedDob) return;
    setSavingDob(true);
    setDobMsg(null);
    try {
      await axios.post(`${API}/api/teacher/teachersettings/teacherinfo`, { dateOfBirth: dob, teacherId });
      setSavedDob(dob);
      setDobMsg({ type: 'success', text: 'Your date of birth was saved.' });
    } catch (err) {
      setDobMsg({ type: 'error', text: err.response?.data?.message || 'We couldn’t save it. Please try again.' });
    } finally {
      setSavingDob(false);
    }
  };

  // ---------- Password (same endpoint + payload) ----------
  const onPw = (e) => {
    setPw((p) => ({ ...p, [e.target.name]: e.target.value }));
    setPwMsg(null);
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (!pw.currentPassword || !pw.newPassword || !pw.confirmPassword) {
      setPwMsg({ type: 'error', text: 'Please fill in all three fields.' });
      return;
    }
    if (pw.newPassword.length < MIN_PASSWORD) {
      setPwMsg({ type: 'error', text: `The new password needs at least ${MIN_PASSWORD} characters.` });
      return;
    }
    if (pw.newPassword !== pw.confirmPassword) {
      setPwMsg({ type: 'error', text: 'The new passwords don’t match.' });
      return;
    }
    if (pw.newPassword === pw.currentPassword) {
      setPwMsg({ type: 'error', text: 'The new password must be different from the current one.' });
      return;
    }
    setSavingPw(true);
    try {
      await axios.post(`${API}/api/teacher/teachersettings/passwordchange`, {
        teacherId,
        currentPassword: pw.currentPassword,
        newPassword: pw.newPassword,
      });
      setPw({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPwMsg({ type: 'success', text: 'Your password was changed.' });
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.message || 'We couldn’t change the password. Check your current password.' });
    } finally {
      setSavingPw(false);
    }
  };

  const fullName = profile ? [profile.firstName, profile.lastName].filter(Boolean).join(' ') : userData?.fullName || '';
  const today = new Date();
  const maxDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  if (loading || !teacherId) {
    return (
      <div className="rmtset" aria-busy="true">
        <div className="rmtset-inner">
          <div className="rmtset-skel rmtset-skel--title" />
          <div className="rmtset-skel rmtset-skel--card" />
          <div className="rmtset-skel rmtset-skel--card" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rmtset">
        <div className="rmtset-inner">
          <div className="rmtset-card rmtset-error">
            <FontAwesomeIcon icon={faCircleExclamation} />
            <p>{loadError}</p>
            <button type="button" className="rmtset-btn" onClick={() => setReload((n) => n + 1)}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rmtset">
      <div className="rmtset-inner">
        <header className="rmtset-head">
          <div>
            <span className="rmtset-eyebrow">My profile</span>
            <h1 className="rmtset-title">{fullName || 'Your account'}</h1>
            <p className="rmtset-sub">Your card on the home page, your photo, your details and password.</p>
          </div>
        </header>

        <div className="rmtset-tabs" role="tablist" aria-label="My profile">
          {TABS.map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`rmtset-tab ${tab === key ? 'is-on' : ''}`}
              onClick={() => openTab(key)}
            >
              <FontAwesomeIcon icon={icon} /> {label}
              {key === 'card' && <span className="rmtset-tab-new">New</span>}
            </button>
          ))}
        </div>

        {tab === 'card' && <PublicProfileForm />}
        {tab === 'photo' && <PhotoChange role="teacher" />}

        {/* ---------- Details ---------- */}
        {tab === 'details' && (
        <form className="rmtset-card" onSubmit={saveDob} noValidate aria-labelledby="rmtset-profile-title">
          <h2 id="rmtset-profile-title" className="rmtset-card-title">
            <FontAwesomeIcon icon={faIdCard} /> Personal details
          </h2>

          <dl className="rmtset-info">
            <div>
              <dt>Username</dt>
              <dd>{profile?.username || '—'}</dd>
            </div>
            <div>
              <dt>Mobile number</dt>
              <dd>{profile?.mobileNumber || '—'}</dd>
            </div>
            <div>
              <dt>First name</dt>
              <dd>{profile?.firstName || '—'}</dd>
            </div>
            <div>
              <dt>Last name</dt>
              <dd>{profile?.lastName || '—'}</dd>
            </div>
            <div className="is-wide">
              <dt>Email</dt>
              <dd>{profile?.email || '—'}</dd>
            </div>
          </dl>
          <p className="rmtset-hint">
            <FontAwesomeIcon icon={faLock} /> To change these, ask the RoboMind admin team.
          </p>

          <div className="rmtset-row">
            <div className="rmtset-field">
              <label htmlFor="rmtset-dob">Date of birth</label>
              <input
                id="rmtset-dob"
                type="date"
                className="rmtset-input"
                value={dob}
                max={maxDate}
                onChange={(e) => {
                  setDob(e.target.value);
                  setDobMsg(null);
                }}
              />
            </div>
          </div>

          <Message msg={dobMsg} />

          <div className="rmtset-actions">
            {dob !== savedDob && (
              <button
                type="button"
                className="rmtset-btn rmtset-btn--ghost"
                onClick={() => {
                  setDob(savedDob);
                  setDobMsg(null);
                }}
                disabled={savingDob}
              >
                Cancel
              </button>
            )}
            <button type="submit" className="rmtset-btn" disabled={dob === savedDob || savingDob}>
              {savingDob ? 'Saving…' : 'Save details'}
            </button>
          </div>
        </form>
        )}

        {/* ---------- Password ---------- */}
        {tab === 'password' && (
        <form className="rmtset-card" onSubmit={savePassword} noValidate aria-labelledby="rmtset-pw-title">
          <h2 id="rmtset-pw-title" className="rmtset-card-title">
            <FontAwesomeIcon icon={faShieldAlt} /> Change password
          </h2>

          <div className="rmtset-row rmtset-row--3">
            <div className="rmtset-field">
              <label htmlFor="rmtset-pw-current">Current password</label>
              <input
                id="rmtset-pw-current"
                name="currentPassword"
                type={showPw ? 'text' : 'password'}
                autoComplete="current-password"
                className="rmtset-input"
                value={pw.currentPassword}
                onChange={onPw}
              />
            </div>
            <div className="rmtset-field">
              <label htmlFor="rmtset-pw-new">New password</label>
              <input
                id="rmtset-pw-new"
                name="newPassword"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                className="rmtset-input"
                value={pw.newPassword}
                onChange={onPw}
                aria-describedby="rmtset-pw-rule"
              />
              <small id="rmtset-pw-rule" className="rmtset-rule">
                At least {MIN_PASSWORD} characters
              </small>
            </div>
            <div className="rmtset-field">
              <label htmlFor="rmtset-pw-confirm">Confirm new password</label>
              <input
                id="rmtset-pw-confirm"
                name="confirmPassword"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                className="rmtset-input"
                value={pw.confirmPassword}
                onChange={onPw}
              />
            </div>
          </div>

          <button type="button" className="rmtset-show" onClick={() => setShowPw((v) => !v)} aria-pressed={showPw}>
            <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} /> {showPw ? 'Hide passwords' : 'Show passwords'}
          </button>

          <Message msg={pwMsg} />

          <div className="rmtset-actions">
            <button type="submit" className="rmtset-btn" disabled={savingPw}>
              {savingPw ? 'Changing…' : 'Change password'}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default TeacherSettings;